import React, { useState } from "react"
import { Plus, CheckSquare, Loader2 } from "lucide-react"
import { Button } from "src/components/ui/button"
import { useToast } from "src/components/ui/toast"
import { TaskCard } from "./TaskCard"
import { TaskFormDrawer } from "./TaskFormDrawer"
import { useTasks } from "../hooks/useTasks"
import { completeTask, deleteTask, snoozeTask } from "../api/tasksApi"

// Embeddable tasks panel for Lead/Deal/Booking detail pages.
//   <TasksPanel parentType="leads" parentId={leadId} members={membersList} />
export function TasksPanel({ parentType, parentId, members = [], title = "Tasks" }) {
  const { toast } = useToast()
  const { tasks, loading, setTasks } = useTasks({ parentType, parentId })
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const open = tasks?.filter((t) => t.status === "open") || []
  const done = tasks?.filter((t) => t.status === "completed") || []

  async function handleComplete(id) {
    try {
      const updated = await completeTask(id)
      setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)))
      toast({ title: "Task completed!" })
    } catch {
      toast({ variant: "destructive", title: "Could not complete task" })
    }
  }

  async function handleDelete(id) {
    try {
      await deleteTask(id)
      setTasks((prev) => prev.filter((t) => t.id !== id))
    } catch {
      toast({ variant: "destructive", title: "Could not delete task" })
    }
  }

  async function handleSnooze(id) {
    try {
      const updated = await snoozeTask(id)
      setTasks((prev) => prev.map((t) => (t.id === id ? updated : t)))
      toast({ title: "Snoozed for 1 hour" })
    } catch {
      toast({ variant: "destructive", title: "Could not snooze task" })
    }
  }

  function handleEdit(task) {
    setEditing(task)
    setDrawerOpen(true)
  }

  function handleSaved(saved) {
    setTasks((prev) => {
      if (!prev) return [saved]
      const exists = prev.find((t) => t.id === saved.id)
      return exists ? prev.map((t) => (t.id === saved.id ? saved : t)) : [saved, ...prev]
    })
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <CheckSquare className="h-4 w-4 text-slate-400" /> {title}
          {open.length > 0 && (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
              {open.length}
            </span>
          )}
        </span>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => { setEditing(null); setDrawerOpen(true) }}>
          <Plus className="h-3.5 w-3.5" /> Add task
        </Button>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
          </div>
        ) : open.length === 0 && done.length === 0 ? (
          <div className="px-2 py-6 text-center">
            <CheckSquare className="mx-auto mb-2 h-7 w-7 text-slate-200" />
            <p className="text-[13px] text-slate-400">No tasks yet</p>
          </div>
        ) : (
          <>
            {open.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onComplete={handleComplete}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSnooze={handleSnooze}
              />
            ))}
            {done.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-[12px] text-slate-400 hover:text-slate-600 select-none">
                  {done.length} completed
                </summary>
                <div className="mt-2 space-y-2">
                  {done.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>

      <TaskFormDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null) }}
        onSaved={handleSaved}
        task={editing}
        parent={parentId ? { type: parentType, id: parentId } : null}
        members={members}
      />
    </div>
  )
}
